import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import GoogleMap from 'google-map-react';
import Col from '@loggi/ui/components/col';
import Display from '@loggi/ui/components/display';
import Button from '@loggi/ui/components/button';
import Text from '@loggi/ui/components/text';
import View from '@loggi/ui/components/view';
import MapMarker from '@loggi/ui/components/map-marker';
import colors from '@loggi/ui/components/utils/colors';
import getWindow from '@loggi/js/get-window';
import mapStyles, { getCurrentTimeStyle } from '../styles';
import fitBounds from '../geo/fit-bounds';
import groupBy from 'lodash/groupBy';
import partition from 'lodash/partition';
import supercluster from 'points-cluster';
import get from 'lodash/get';

import Clipart from '../components/clipart';

const MAP_STYLE_DAY = 'day';
const MAP_STYLE_NIGHT = 'night';
const MAP_STYLE_AUTO = 'auto';
const MAP_STYLES = [MAP_STYLE_DAY, MAP_STYLE_NIGHT, MAP_STYLE_AUTO];
const MAP_DEFAULT_KEY = process.env.GOOGLE_MAPS_FRONTEND_API_KEY;

const REF = 'map';
const EMPTY_POINT = { lat: 0, lng: 0 };
const containerStyle = {
  width: '100%',
  height: '100%',

  // to avoid Map to hide some buttons or crash because of the repainting on safari
  // it's needed to force the render on GPU using translateZ and position
  position: 'relative',
  transform: 'translateZ(0)'
};

const RENDER_MAP = 'Confira seu pedido no mapa';

function getMapStyle(style) {
  return style === MAP_STYLE_AUTO ? getCurrentTimeStyle() : mapStyles[style];
}

function getMapApiKey() {
  return (
    get(
      getWindow(),
      'loggi.bootstrapData.google_maps_api_key',
      MAP_DEFAULT_KEY
    ) || MAP_DEFAULT_KEY
  );
}

class Map extends React.Component {
  static displayName = 'Map';

  static propTypes = {
    style: PropTypes.oneOf(MAP_STYLES),
    options: PropTypes.object,
    fitBounds: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
    children: PropTypes.oneOfType([
      PropTypes.node,
      PropTypes.arrayOf(PropTypes.node)
    ]),
    settings: PropTypes.shape({
      key: PropTypes.string,
      libraries: PropTypes.string,
      language: PropTypes.string
    }),
    channel: PropTypes.string,
    bounds: PropTypes.shape({
      width: PropTypes.number,
      height: PropTypes.number
    }),
    hoverDistance: PropTypes.number,
    initialZoom: PropTypes.number,
    zoom: PropTypes.number,
    initialCenter: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number
    }),
    center: PropTypes.shape({
      lat: PropTypes.number,
      lng: PropTypes.number
    }),
    draggable: PropTypes.bool,
    distanceToMouse: PropTypes.func,
    cluster: PropTypes.bool,
    clusterColorPriority: PropTypes.arrayOf(PropTypes.string),
    clusterOptions: PropTypes.shape({
      minZoom: PropTypes.number,
      maxZoom: PropTypes.number,
      clusterRadius: PropTypes.number
    }),
    clusterLabelFormatter: PropTypes.func,
    clusterFilter: PropTypes.func,
    margin: PropTypes.array,
    onClick: PropTypes.func,
    onChildClick: PropTypes.func,
    onChildMouseEnter: PropTypes.func,
    onChildMouseLeave: PropTypes.func,
    onChildMouseMove: PropTypes.func,
    onChildMouseUp: PropTypes.func,
    onChildMouseDown: PropTypes.func,
    initialShouldRenderMap: PropTypes.bool
  };

  static defaultProps = {
    style: MAP_STYLE_DAY,
    fitBounds: false,
    bounds: null,
    draggable: true,
    hoverDistance: 65,
    margin: null,
    cluster: false,
    clusterColorPriority: [
      colors.type.DANGER,
      colors.type.WARNING,
      colors.type.INFO,
      colors.type.ACTIVE,
      colors.type.SUCCESS
    ],
    clusterOptions: {
      minZoom: 3,
      maxZoom: 15,
      clusterRadius: 60
    },
    settings: {
      key: getMapApiKey(),
      libraries: 'geometry,places',
      language: 'pt-br',
      v: 'quarterly'
    },
    channel: 'elke_map_default_channel',
    clusterLabelFormatter: ({ numPoints }) => `+${numPoints}`,
    clusterFilter: ({ type }) => type === MapMarker,
    initialShouldRenderMap: false
  };

  constructor(props) {
    super(props);

    this._api = null;
    this._mapsListeners = [];

    this._onGoogleApiLoaded = this._onGoogleApiLoaded.bind(this);
    this._onChange = this._onChange.bind(this);
    this._onResize = this._onResize.bind(this);
    this._onAddMapFeature = this._onAddMapFeature.bind(this);
    this._onRemoveMapFeature = this._onRemoveMapFeature.bind(this);
    this._getOptions = this._getOptions.bind(this);

    this._onClick = this._mouseEvent.bind(this, 'onClick');
    this._onChildClick = this._mouseEvent.bind(this, 'onChildClick');
    this._onChildMouseEnter = this._mouseEvent.bind(this, 'onChildMouseEnter');
    this._onChildMouseLeave = this._mouseEvent.bind(this, 'onChildMouseLeave');
    this._onChildMouseMove = this._mouseEvent.bind(this, 'onChildMouseMove');
    this._onChildMouseUp = this._mouseEvent.bind(this, 'onChildMouseUp');
    this._onChildMouseDown = this._mouseEvent.bind(this, 'onChildMouseDown');

    this.state = {
      isApiLoaded: false,
      bounds: { width: 0, height: 0 },
      geoBounds: { se: EMPTY_POINT, nw: EMPTY_POINT },
      zoom: props.initialZoom || 1,
      center: props.initialCenter || EMPTY_POINT,
      mapFeatures: [],
      shouldRenderMap: props.initialShouldRenderMap
    };
  }

  _onGoogleApiLoaded(api) {
    this._api = api;
    this._addMapsListener('addfeature', this._onAddMapFeature);
    this._addMapsListener('removefeature', this._onRemoveMapFeature);
    this.setState({ isApiLoaded: true });
  }

  _addMapsListener(event, listener) {
    if (!this._api) {
      return false;
    }
    const { map } = this._api;
    this._mapsListeners.push(map.data.addListener(event, listener));
    return true;
  }

  _onAddMapFeature({ feature }) {
    const { mapFeatures } = this.state;
    mapFeatures.push(feature);
    this.setState({ mapFeatures });
  }

  _onRemoveMapFeature({ feature }) {
    const { mapFeatures } = this.state;
    const index = mapFeatures.findIndex(f => f === feature);
    if (index > -1) {
      mapFeatures.splice(index, 1);
      this.setState({ mapFeatures });
    }
  }

  _onChange({ zoom, center, bounds }) {
    this.setState({ zoom, center, geoBounds: bounds });
  }

  _onResize() {
    const container = this.refs[REF] || null;
    if (container === null) {
      return false;
    }

    const domNode = ReactDOM.findDOMNode(container) || null;
    if (domNode === null) {
      return false;
    }

    const { offsetWidth: width, offsetHeight: height } = domNode;
    this.setState({ bounds: { width, height } });
    return true;
  }

  _getBounds() {
    return this.props.bounds || this.state.bounds;
  }

  _getCenterAndZoom(items) {
    let { center, zoom } = this.state;

    if (this.props.fitBounds) {
      const mapFeaturesCoordinates = [];
      this.state.mapFeatures.forEach(f => {
        if (f.getGeometry) {
          f.getGeometry().forEachLatLng(p => {
            mapFeaturesCoordinates.push({ lat: p.lat(), lng: p.lng() });
          });
        }
        // TODO (glaet) make fit to bounds work with polylines again
        // if (f.getPath) {
        //   f.getPath().forEach(p => {
        //     mapFeaturesCoordinates.push({lat: p.lat(), lng: p.lng()});
        //   });
        // }
      });

      let coordinates = items
        .filter(c => c.props && c.props.lat && c.props.lng)
        .map(c => {
          const { lat, lng } = c.props;
          return {
            lat,
            lng,
            type: c.type,
            key: c.key
          };
        });

      if (typeof this.props.fitBounds === 'function') {
        coordinates = coordinates.filter(this.props.fitBounds);
      }

      const result = fitBounds(
        coordinates.concat(mapFeaturesCoordinates),
        this._getBounds(),
        center,
        zoom
      );
      center = result.center;
      zoom = result.zoom;
    }

    return { center, zoom };
  }

  _getOptions(maps) {
    const options = this.props.options || {};
    return Object.assign(
      {
        styles: getMapStyle(this.props.style)
      },
      typeof options === 'function' ? options(maps) : options
    );
  }

  _getMapItems() {
    const itemsByPos = groupBy(
      React.Children.toArray(this.props.children),
      i => `${i.props.lat}:${i.props.lng}`
    );
    const mapItems = [];
    Object.keys(itemsByPos).forEach(key => {
      const length = itemsByPos[key].length;
      itemsByPos[key].forEach((item, index) => {
        let deg = -((length - 1) * 30) + index * 60;
        const style = {
          transform: `rotate(${deg}deg)`
        };

        const props = {
          style,
          key: item.key || `${key}${index}`
        };

        if (item.type && item.type.gmapsProxy) {
          // We only send GMaps API object for components
          // that explicit mark themselves as proxy components
          props.api = this._api;
          props.zoom = this.state.zoom;
          props.center = this.state.center;
          props.onAddMapFeature = this._onAddMapFeature;
          props.onRemoveMapFeature = this._onRemoveMapFeature;
        }

        mapItems.push(React.cloneElement(item, props));
      });
    });
    return mapItems;
  }

  _getMapChildren(mapItems) {
    if (!this.props.cluster) {
      return mapItems;
    }

    const [clusterItems, otherItems] = partition(
      mapItems,
      this.props.clusterFilter
    );

    const getCluster = supercluster(
      clusterItems.map(item => ({
        lat: item.props.lat,
        lng: item.props.lng,
        item
      })),
      this.props.clusterOptions
    );

    const { clusterColorPriority } = this.props;
    const clusters = getCluster({
      bounds: this.state.geoBounds,
      center: this.state.center,
      zoom: this.state.zoom
    });

    return otherItems.concat(
      clusters.map((props, index) => {
        if (props.numPoints === 1) {
          return props.points[0].item;
        }

        const label = this.props.clusterLabelFormatter(props);
        const { wy: lat, wx: lng } = props;
        let color = colors.type.ACTIVE;
        for (let i = 0; i < clusterColorPriority.length; i++) {
          const c = clusterColorPriority[i];
          if (props.points.find(({ item }) => c === item.props.color)) {
            color = c;
            break;
          }
        }

        return (
          <MapMarker
            key={`group-${index}`}
            label={label}
            color={color}
            lat={lat}
            lng={lng}
            group={true}
          />
        );
      })
    );
  }

  _mouseEvent(event, ...args) {
    const callback = this.props[event] || null;
    if (callback !== null) {
      callback.apply(null, args.concat(this._api));
    }
  }

  componentDidMount() {
    const $window = getWindow();
    if ($window !== null) {
      $window.addEventListener('resize', this._onResize);
    }
    this._onResize();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (this.props.initialCenter !== nextProps.initialCenter) {
      this.setState({ center: nextProps.initialCenter });
    }
  }

  componentWillUnmount() {
    const $window = getWindow();
    if ($window !== null) {
      $window.removeEventListener('resize', this._onResize);
    }

    if (this._api) {
      const { maps } = this._api;
      if (maps) {
        this._mapsListeners.forEach(l => maps.event.removeListener(l));
      }
      this._mapsListeners = null;
      this._api = null;
    }
  }

  render() {
    const { settings, channel } = this.props;
    const { shouldRenderMap } = this.state;
    const bootstrapURLKeys = { ...settings, channel };
    const mapItems = this._getMapItems();
    const { center, zoom } = this._getCenterAndZoom(mapItems);

    return (
      <View
        ref={REF}
        style={containerStyle}
        backgroundColor="smoke-050"
        justifyContent="center"
        alignItems="center"
      >
        <Display when={!shouldRenderMap}>
          <Col size={{ xs: 10, md: 6, lg: 6, xl: 3 }}>
            <Col size={12} justifyContent="center">
              <Clipart />
            </Col>
            <Col
              size={12}
              justifyContent="center"
              gutter={{ top: 'onehalf', bottom: 'onehalf' }}
            >
              <Text>{RENDER_MAP}</Text>
            </Col>
            <Col size={6} offset={3}>
              <Button
                block
                style="outline"
                onPress={() => {
                  this.setState({ shouldRenderMap: !shouldRenderMap });
                }}
                value="Ver Mapa"
              />
            </Col>
          </Col>
        </Display>

        <Display when={shouldRenderMap}>
          <GoogleMap
            yesIWantToUseGoogleMapApiInternals={true}
            bootstrapURLKeys={bootstrapURLKeys}
            center={this.props.center || center}
            zoom={this.props.zoom || zoom}
            options={this._getOptions}
            hoverDistance={this.props.hoverDistance}
            distanceToMouse={this.props.distanceToMouse}
            margin={this.props.margin}
            draggable={this.props.draggable}
            onChange={this._onChange}
            onChildMouseEnter={this._onChildMouseEnter}
            onChildMouseLeave={this._onChildMouseLeave}
            onChildMouseDown={this._onChildMouseDown}
            onChildMouseUp={this._onChildMouseUp}
            onChildMouseMove={this._onChildMouseMove}
            onChildClick={this._onChildClick}
            onClick={this._onClick}
            onGoogleApiLoaded={this._onGoogleApiLoaded}
          >
            {this._getMapChildren(mapItems)}
          </GoogleMap>
        </Display>
      </View>
    );
  }
}

Map.STYLE_DAY = MAP_STYLE_DAY;
Map.STYLE_NIGHT = MAP_STYLE_NIGHT;
Map.STYLE_AUTO = MAP_STYLE_AUTO;

export default Map;
